/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Alert, Space, Typography } from 'antd';

// Misc imports
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { LANGUAGE_OPTIONS } from '../../constants/main';

/**
 * A warning alert to show when the user is editing a bot in a secondary language.
 * @param {Boolean} showLanguageSwitchPrompt Whether to show the prompt to switch back to the primary language.
 * @param {String} className The class name to apply to the component.
 * @returns {JSX.Element} The secondary language alert component.
 */
const SecondaryLanguageAlert = ({ showLanguageSwitchPrompt, className = '', ...restProps }) => {
  const { currentBotLanguage, isEditingPrimaryLanguage, changeBotLanguage } = useBotLanguage();
  const { t } = useTranslation();

  return !isEditingPrimaryLanguage ? (
    <div id="secondary-language-alert">
      <Alert
        className={className}
        type="warning"
        message={(
          <Space size={4}>
            <Typography.Text>
              {t('You are currently editing agent contents in')} <strong>{LANGUAGE_OPTIONS.find(lang => lang.key === currentBotLanguage)?.label}</strong>.
            </Typography.Text>

            {
              showLanguageSwitchPrompt ? (
                <span>
                  <Typography.Text>
                    {t('To go back to the primary language click')}
                  </Typography.Text>

                  <Typography.Link
                    strong
                    onClick={() => changeBotLanguage(null)}
                    className="pl-1"
                  >
                    {t('here')}
                  </Typography.Link>.
                </span>
              ) : null
            }
          </Space>
        )}
        {...restProps}
      />
    </div>
  ): null;
};

SecondaryLanguageAlert.propTypes = {
  className: PropTypes.string,
  showLanguageSwitchPrompt: PropTypes.bool
};

export default SecondaryLanguageAlert;
